import React from "react";
import { cn } from "@/shared/utils/shadcn";

interface ChartSectorProps {
  id?: string;
  startAngle: number;
  endAngle: number;
  midX: number;
  midY: number;
  sectorRadius: number;
  titleRadius: number;
  score: number;
  title: string;
  color: string;
  onClick: () => void;
}

const SectorWedgeColorVariants = {
  grey: "stroke-grey-300",
  green0: "stroke-green-500",
  green1: "stroke-green-300",
  green2: "stroke-green-200",
  yellow0: "stroke-orange-500",
  yellow1: "stroke-orange-300",
  yellow2: "stroke-orange-200",
};

const ChartSector: React.FC<ChartSectorProps> = ({
  id,
  startAngle,
  endAngle,
  midX,
  midY,
  sectorRadius,
  titleRadius,
  score,
  title,
  color,
  onClick,
}) => {
  const wedgeAngle = endAngle - startAngle;
  const scoreAngle = startAngle + wedgeAngle / 2;

  const x1 = midX + sectorRadius * Math.cos((Math.PI * startAngle) / 180);
  const y1 = midY + sectorRadius * Math.sin((Math.PI * startAngle) / 180);
  const x2 = midX + sectorRadius * Math.cos((Math.PI * endAngle) / 180);
  const y2 = midY + sectorRadius * Math.sin((Math.PI * endAngle) / 180);

  const tx1 = midX + titleRadius * Math.cos((Math.PI * startAngle) / 180);
  const ty1 = midY + titleRadius * Math.sin((Math.PI * startAngle) / 180);
  const tx2 = midX + titleRadius * Math.cos((Math.PI * endAngle) / 180);
  const ty2 = midY + titleRadius * Math.sin((Math.PI * endAngle) / 180);

  const scoreX = midX + sectorRadius * Math.cos((Math.PI * scoreAngle) / 180);
  const scoreY = midY + sectorRadius * Math.sin((Math.PI * scoreAngle) / 180);

  const isBottomHalf = scoreAngle < 180;

  const wrapText = (text: string, maxLength: number): string[] => {
    const words = text.split(/\s+/).filter((word) => word.length > 0);
    const lines: string[] = [];
    let currentLine = "";

    words.forEach((word) => {
      const potentialLine = currentLine ? `${currentLine} ${word}` : word;
      if (potentialLine.length <= maxLength) {
        currentLine = potentialLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.filter((line) => line.trim().length > 0);
  };

  const wrappedTitle = wrapText(title, 12);

  const getTitleLineOffset = (index: number) => {
    if (wrapText.length <= 1) return 0;

    if (isBottomHalf) {
      if (index === 0) return 0;
      return 1.5;
    }

    return (index - (wrappedTitle.length - 1) / 2) * 1.5;
  };

  return (
    <g className="group cursor-pointer" onClick={onClick}>
      <path
        id={`${id}-title-path`}
        className="fill-none stroke-none"
        d={
          isBottomHalf
            ? `M${tx2} ${ty2} A${titleRadius} ${titleRadius} 0 0 0 ${tx1} ${ty1}`
            : `M${tx1} ${ty1} A${titleRadius} ${titleRadius} 0 0 1 ${tx2} ${ty2}`
        }
      />
      <path
        className="stroke-grey-500 stroke-opacity-10 stroke-[0.05] fill-transparent transition-all duration-300 ease-in-out group-hover:fill-white/40 group-hover:stroke-opacity-30"
        d={`M${midX} ${midY} L${x1} ${y1} A${sectorRadius} ${sectorRadius} 0 0 1 ${x2} ${y2} z`}
      />
      <path
        className={cn(
          "fill-none stroke-grey-300 stroke-[1.5] transition-all duration-300 ease-in-out group-hover:stroke-[1.8]",
          SectorWedgeColorVariants[
            color as keyof typeof SectorWedgeColorVariants
          ]
        )}
        d={`M${x1} ${y1} A${sectorRadius} ${sectorRadius} 0 0 1 ${x2} ${y2}`}
      />
      <g transform={`translate(${scoreX}, ${scoreY})`}>
        <circle
          r="1.5"
          className="fill-primary opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"
        />
        <text
          className="text-[1.5px] font-medium fill-white opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"
          textAnchor="middle"
          y=".5"
        >
          {Math.round(score)}
        </text>
      </g>
      <g>
        {wrappedTitle.map((line, index) => {
          const yOffset = getTitleLineOffset(index);
          return (
            <text
              key={index}
              className="text-[1.5px] font-normal fill-grey-600 fill-opacity-60 transition-all duration-300 ease-in-out group-hover:fill-primary"
            >
              <textPath
                xlinkHref={`#${id}-title-path`}
                startOffset="50%"
                textAnchor="middle"
              >
                <tspan dy={`${yOffset}em`}>{line}</tspan>
              </textPath>
            </text>
          );
        })}
      </g>
    </g>
  );
};

export default ChartSector;
